import { useAppModalViewStore } from "../../Stores/AppModalView";
import { useSessionStore } from "../../Stores/Session";
import { useTicketStore } from "../../Stores/Ticket";
import "./BotActionInputCalendly.scss";

const isCalendlyLink = (link: string) => {
  try {
    const hostname = new URL(link).hostname;
    return hostname === "calendly.com" || hostname.endsWith(".calendly.com");
  } catch (e) {
    return false;
  }
};

// Mirrors what Cal.com's own embed snippet loads: the `/embed` variant of the
// booking page with an `embed` namespace, so the page reports bookings to us.
const buildCalcomEmbedUrl = (link: string, name?: string, email?: string) => {
  const url = new URL(link);
  if (url.hostname === "cal.com") {
    // Cal.com's embed does the same — cal.com rewrites break assets in Safari.
    url.hostname = "app.cal.com";
  }
  if (!url.pathname.endsWith("/embed")) {
    url.pathname = `${url.pathname.replace(/\/$/, "")}/embed`;
  }
  url.searchParams.set("embed", "gleap");
  url.searchParams.set("embedType", "inline");
  if (name) {
    url.searchParams.set("name", name);
  }
  if (email) {
    url.searchParams.set("email", email);
  }
  return url.toString();
};

// The configured link may already carry a query (utm_campaign=…): add ours to it.
const buildCalendlyEmbedUrl = (link: string, name?: string, email?: string) => {
  const url = new URL(link);
  url.searchParams.set("embed_domain", "messenger-app.gleap.io");
  url.searchParams.set("embed_type", "Inline");
  url.searchParams.set("hide_gdpr_banner", "1");
  if (name) url.searchParams.set("name", name);
  if (email) url.searchParams.set("email", email);
  url.searchParams.set("utm_source", "Gleap");
  url.searchParams.set("utm_medium", "Messenger");
  return url.toString();
};

function BotActionInputCalendly({ action }: { action: any }) {
  const appModalViewStore = useAppModalViewStore();
  const session = useSessionStore.getState().session;
  const ticketStore = useTicketStore();

  if (!action) {
    return null;
  }

  const openCalendly = () => {
    let embedUrl: string;
    try {
      embedUrl = buildCalendlyEmbedUrl(
        action.calendlyLink,
        session?.name,
        session?.email
      );
    } catch (e) {
      return;
    }
    appModalViewStore.openApp(
      embedUrl,
      "Calendly",
      () => {},
      (event) => {
        if (event && event.event === "calendly.event_scheduled") {
          const uri = event.payload?.event?.uri;

          ticketStore.answerBotAction(
            {
              calendlyUri: uri,
            },
            []
          );

          appModalViewStore.closeApp();
        }
      }
    );
  };

  const openCalcom = () => {
    let embedUrl: string;
    try {
      embedUrl = buildCalcomEmbedUrl(
        action.calendlyLink,
        session?.name,
        session?.email
      );
    } catch (e) {
      return;
    }

    // Cal.com fires both `bookingSuccessfulV2` (title + times) and the legacy
    // `bookingSuccessful` (adds the organizer; older self-hosted versions only
    // send this one). Merge whatever arrives within a short window.
    let booking: any = null;
    let answerTimer: ReturnType<typeof setTimeout> | undefined;
    let answered = false;

    const submitBooking = () => {
      if (answered || !booking) {
        return;
      }
      answered = true;
      if (answerTimer) {
        clearTimeout(answerTimer);
      }

      ticketStore.answerBotAction(
        {
          calcomBooking: booking,
        },
        []
      );
    };

    appModalViewStore.openApp(
      embedUrl,
      "Schedule a meeting",
      // Closed before the merge window ran out — still record the booking.
      submitBooking,
      (data, event) => {
        if (!data || data.originator !== "CAL") {
          return;
        }

        if (data.type === "__iframeReady") {
          // The booking page stays hidden until its parent acknowledges it.
          (event?.source as Window | null)?.postMessage(
            { originator: "CAL", method: "parentKnowsIframeReady", arg: {} },
            event?.origin ?? "*"
          );
          return;
        }

        if (
          data.type !== "bookingSuccessfulV2" &&
          data.type !== "bookingSuccessful"
        ) {
          return;
        }

        const payload = data.data ?? {};
        booking = {
          ...booking,
          title:
            booking?.title ??
            payload.title ??
            payload.booking?.title ??
            payload.eventType?.title,
          startTime:
            booking?.startTime ??
            payload.startTime ??
            payload.booking?.startTime ??
            payload.date,
          organizer: booking?.organizer ?? payload.organizer,
        };

        if (answerTimer) {
          return;
        }
        answerTimer = setTimeout(() => {
          submitBooking();
          appModalViewStore.closeApp();
        }, 400);
      }
    );
  };

  return (
    <div className="bot-action-calendly-input">
      <div className="bot-action-calendly-input-label">{action.intro}</div>
      <button
        className="action-button"
        onClick={() => {
          if (!action.calendlyLink) {
            return;
          }

          if (isCalendlyLink(action.calendlyLink)) {
            openCalendly();
          } else {
            openCalcom();
          }
        }}
        type="button"
      >
        {action.buttonText ?? "Book now"}
      </button>
    </div>
  );
}

export default BotActionInputCalendly;
